import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import ClientDashboard from './pages/ClientDashboard';
import PublicAgencyPage from './pages/PublicAgencyPage';
import TourViewPage from './pages/TourViewPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookieConsent from './components/CookieConsent';
import LandingPage from './pages/LandingPage';
import { useAuth } from './context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import api from './api/axios';
import LoadingSpinner from './components/LoadingSpinner';

const PrivateRoute = ({ children, role }) => {
    const { user } = useAuth();
    if (!user) return <Navigate to="/login" />;
    if (role && user.role !== role) return <Navigate to="/" />;
    return children;
};

function App() {
    const { i18n } = useTranslation();

    const [isSyncing, setIsSyncing] = useState(true);

    useEffect(() => {
        const syncLanguageWithServer = async () => {
            try {
                // Priority 1: URL query parameter
                const params = new URLSearchParams(window.location.search);
                const queryLang = params.get('lang');
                if (queryLang && ['ar', 'en', 'es'].includes(queryLang)) {
                    if (!i18n.language.startsWith(queryLang)) {
                        await i18n.changeLanguage(queryLang);
                    }
                    localStorage.setItem('i18nextLngManual', 'true');
                    setIsSyncing(false);
                    return; // URL override is highest priority
                }

                // Priority 2: Manual local storage preference
                const isManual = localStorage.getItem('i18nextLngManual') === 'true';
                const hasSavedLng = localStorage.getItem('i18nextLng');

                // If user already made a manual choice, respect it
                if (isManual && hasSavedLng) {
                    setIsSyncing(false);
                    return;
                }

                // Priority 3: Server default configuration
                const response = await api.get('/config/public', { timeout: 3000 });
                if (response.data && response.data.language) {
                    const serverDefault = response.data.language;

                    if (!i18n.language.startsWith(serverDefault)) {
                        await i18n.changeLanguage(serverDefault);
                    }
                }
            } catch (err) {
                console.warn('Failed to sync language with server default:', err);
            } finally {
                setIsSyncing(false);
            }
        };

        syncLanguageWithServer();
    }, [i18n]);

    if (isSyncing) return <LoadingSpinner />;

    return (
        <Router>
            <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<Login />} />
                <Route
                    path="/admin"
                    element={
                        <PrivateRoute role="admin">
                            <AdminDashboard />
                        </PrivateRoute>
                    }
                />
                <Route
                    path="/client"
                    element={
                        <PrivateRoute role="client">
                            <ClientDashboard />
                        </PrivateRoute>
                    }
                />
                <Route path="/agency/:slug" element={<PublicAgencyPage />} />
                <Route path="/tour/:id" element={<TourViewPage />} />
                <Route path="/view-tour/:id" element={<TourViewPage />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
            </Routes>
            <CookieConsent />
        </Router>
    )
}

export default App
